import JSZip from 'jszip';
import { supabase } from '@core/supabase/client';

const PAGE_SIZE = 500;

function csvEscapeCell(v: unknown): string {
  if (v === undefined || v === null) return '';
  const str = typeof v === 'object' ? JSON.stringify(v) : String(v);
  if (/[,"\r\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

/** Flattens rows to CSV; handles ragged keys across rows */
export function rowsToCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return '';
  const keySet = new Set<string>();
  for (const r of rows) {
    for (const k of Object.keys(r)) keySet.add(k);
  }
  const keys = [...keySet];
  const header = keys.map((k) => csvEscapeCell(k)).join(',');
  const lines = rows.map((r) => keys.map((k) => csvEscapeCell(r[k])).join(','));
  return [header, ...lines].join('\n');
}

async function fetchAllByOrgId(table: string, orgId: string): Promise<Record<string, unknown>[]> {
  const all: Record<string, unknown>[] = [];
  let from = 0;
  for (;;) {
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .eq('org_id', orgId)
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw new Error(`${table}: ${error.message}`);
    const rows = (data ?? []) as Record<string, unknown>[];
    all.push(...rows);
    if (rows.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return all;
}

async function fetchOrganizationRow(orgId: string): Promise<Record<string, unknown>[]> {
  const { data, error } = await supabase.from('ggj_b2b_organizations').select('*').eq('id', orgId).maybeSingle();
  if (error) throw new Error(`ggj_b2b_organizations: ${error.message}`);
  return data ? [data as Record<string, unknown>] : [];
}

const EXPORT_TABLES = [
  'ggj_b2b_org_users',
  'ggj_b2b_clients',
  'ggj_b2b_candidates',
  'ggj_b2b_jobs',
  'ggj_b2b_applications',
  'ggj_b2b_requisitions',
] as const;

/**
 * Builds a ZIP of CSV extracts for this tenant (org-scoped rows + organisation profile).
 * Used before tenant exit so org-specific rows can be purged later while schema remains.
 */
export async function downloadOrgTenantFullExportZip(params: {
  orgId: string;
  orgName?: string | null;
}): Promise<void> {
  const { orgId, orgName } = params;
  const zip = new JSZip();
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

  const orgRows = await fetchOrganizationRow(orgId);
  zip.file('organization_profile.csv', rowsToCsv(orgRows));

  for (const table of EXPORT_TABLES) {
    const rows = await fetchAllByOrgId(table, orgId);
    const short = table.replace('ggj_b2b_', '');
    zip.file(`${short}.csv`, rowsToCsv(rows));
  }

  zip.file(
    'README.txt',
    [
      'GoGrabJob Business — full organisation data export (CSV bundle)',
      '',
      `Organisation: ${orgName ?? orgId}`,
      `Exported (UTC): ${new Date().toISOString()}`,
      '',
      'This ZIP contains one UTF-8 CSV per table (plus organisation_profile.csv).',
      'Store it securely. After tenant exit is completed, org-specific row data may be',
      'removed from our systems; database objects and automation remain.',
      '',
    ].join('\n'),
  );

  const blob = await zip.generateAsync({ type: 'blob' });
  const slug = (orgName ?? 'org')
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 48);
  const filename = `ggj_org_full_export_${slug || 'org'}_${stamp}.zip`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
